import { GATEWAY_ORIGINS } from '../../config/Config';

// Claude (or another MCP client) sends people to /login?oauth_redirect=<gateway authorize URL>.
// The request is kept in sessionStorage so it survives login, Google sign-in and verification,
// then the consent screen at OAUTH_CONSENT_PATH reads it back.

export const OAUTH_CONSENT_PATH = '/oauth/consent';

const STORAGE_KEY = 'portalOAuthRequest';
const AUTHORIZE_PATH = '/client/oauth/authorize';
// A request left this long is stale: a later, unrelated login goes to the dashboard as usual
const MAX_AGE_MS = 30 * 60 * 1000;

// Forwarded exactly as received; absent ones are left out
const OAUTH_PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'scope',
  'state',
  'code_challenge',
  'code_challenge_method',
  'resource',
];

// profile and openid share one label, so the consent screen shows it once (see scopeLabels)
export const OAUTH_SCOPE_LABELS = {
  'bridged.read': 'View your Bridged data',
  'bridged.write': 'Create and change data in your Bridged account',
  profile: 'Share your name and email',
  openid: 'Share your name and email',
};

/** Plain-language labels for a list of scopes, each label once and in order. */
export function scopeLabels(scopes) {
  return [...new Set(scopes.map(scope => OAUTH_SCOPE_LABELS[scope] || scope))];
}

// Only an authorize URL on one of our gateways is accepted; anything else is ignored.
function parseAuthorizeUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (!GATEWAY_ORIGINS.includes(url.origin)) return null;
  if (!url.pathname.replace(/\/+$/, '').endsWith(AUTHORIZE_PATH)) return null;

  const params = {};
  OAUTH_PARAMS.forEach(key => {
    if (url.searchParams.has(key)) params[key] = url.searchParams.get(key);
  });
  return { origin: url.origin, params };
}

/** Called once at boot: stores a valid `oauth_redirect` from the login page URL. */
export function captureOAuthRequest() {
  if (window.location.pathname.replace(/\/+$/, '') !== '/login') return;
  const value = new URLSearchParams(window.location.search).get('oauth_redirect');
  if (!value) return;

  const request = parseAuthorizeUrl(value);
  if (!request) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...request, savedAt: Date.now() }));
  } catch {}
}

export function getPendingOAuthRequest() {
  try {
    const request = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (!request?.origin || !request?.params) return null;
    if (Date.now() - request.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return request;
  } catch {
    return null;
  }
}

export function clearPendingOAuthRequest() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/** Where to go after logging in: the consent screen while a connection is waiting. */
export function getPostLoginPath(fallback = '/') {
  return getPendingOAuthRequest() ? OAUTH_CONSENT_PATH : fallback;
}
